// src/pages/ForgotPassword.jsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Phone, Lock, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAuthApi } from "../hooks/useAuthApi";
import { useToast } from "@/hooks/use-toast";

// Schemas
const requestSchema = z.object({
    identifier: z.string().min(1, "Email or Phone is required"),
}).refine((data) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    return emailRegex.test(data.identifier) || phoneRegex.test(data.identifier);
}, {
    message: "Invalid email or phone number (10 digits)",
    path: ["identifier"],
});

const resetSchema = z.object({
    otp: z.string().length(6, "OTP must be 6 digits"),
    new_password: z.string().min(8, "Password must be at least 8 characters")
        .regex(/[A-Z]/, "Must contain uppercase letter")
        .regex(/[a-z]/, "Must contain lowercase letter")
        .regex(/[0-9]/, "Must contain number")
        .regex(/[^A-Za-z0-9]/, "Must contain special character"),
    confirm_password: z.string()
}).refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
});

const ForgotPassword = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { forgotPassword, resetPassword, loading } = useAuthApi();

    // Steps: 1: Request OTP, 2: Verify & Reset
    const [step, setStep] = useState(1);
    const [identifier, setIdentifier] = useState(""); // Email or Phone
    const [isMobile, setIsMobile] = useState(false);

    const {
        register: registerRequest,
        handleSubmit: handleSubmitRequest,
        formState: { errors: requestErrors },
        setError: setRequestError
    } = useForm({
        resolver: zodResolver(requestSchema)
    });

    const {
        register: registerReset,
        handleSubmit: handleSubmitReset,
        formState: { errors: resetErrors },
        setError: setResetError
    } = useForm({
        resolver: zodResolver(resetSchema)
    });

    const onRequestSubmit = async (data) => {
        const val = data.identifier;
        const isPhone = /^\d{10}$/.test(val);
        setIsMobile(isPhone);

        const payload = isPhone ? { phone: val, email: null } : { email: val, phone: null };

        try {
            await forgotPassword(payload);
            setIdentifier(val);
            setStep(2);
            toast({ title: "OTP Sent", description: `OTP sent to your ${isPhone ? 'mobile' : 'email'}.` });
        } catch (error) {
            console.error("Forgot Password error:", error);
            let errorMessage = "Failed to send OTP. User may not exist.";

            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === "string") {
                    errorMessage = detail;
                } else if (Array.isArray(detail)) {
                    // Handle Pydantic validation errors
                    errorMessage = detail.map(err => err.msg).join(", ");
                } else if (typeof detail === "object") {
                    errorMessage = JSON.stringify(detail);
                }
            }

            setRequestError("root", {
                message: errorMessage
            });
        }
    };

    const onResetSubmit = async (data) => {
        const payload = {
            otp: data.otp,
            new_password: data.new_password,
            confirm_password: data.confirm_password,
            ...(isMobile ? { phone: identifier } : { email: identifier })
        };

        try {
            await resetPassword(payload);
            toast({ title: "Success", description: "Password has been reset successfully." });
            setTimeout(() => navigate("/auth"), 2000);
        } catch (error) {
            console.error("Reset Password error:", error);
            let errorMessage = "Failed to reset password. Invalid OTP or rules.";

            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === "string") {
                    errorMessage = detail;
                } else if (Array.isArray(detail)) {
                    // Handle Pydantic validation errors
                    errorMessage = detail.map(err => err.msg).join(", ");
                } else if (typeof detail === "object") {
                    errorMessage = JSON.stringify(detail);
                }
            }

            setResetError("root", {
                message: errorMessage
            });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center p-4">
            <Card className="w-full max-w-md shadow-xl border-orange-200">
                <CardHeader>
                    <div className="flex items-center mb-4">
                        <Link to="/auth" className="text-gray-500 hover:text-orange-600 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                    </div>
                    <CardTitle className="text-2xl text-center">
                        {step === 1 ? "Forgot Password" : "Reset Password"}
                    </CardTitle>
                    <CardDescription className="text-center">
                        {step === 1
                            ? "Enter your email or mobile number to receive an OTP"
                            : `Enter the specific OTP sent to ${identifier} and your new password`
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {step === 1 ? (
                        <form onSubmit={handleSubmitRequest(onRequestSubmit)} className="space-y-4">
                            <div>
                                <Label htmlFor="identifier">Email or Mobile Number</Label>
                                <div className="relative mt-1">
                                    <Input
                                        id="identifier"
                                        placeholder="Enter email or 10-digit mobile"
                                        {...registerRequest("identifier")}
                                        disabled={loading}
                                    />
                                </div>
                                {requestErrors.identifier && (
                                    <p className="text-red-500 text-sm mt-1">{requestErrors.identifier.message}</p>
                                )}
                            </div>

                            {requestErrors.root && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{requestErrors.root.message}</p>
                                </div>
                            )}

                            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 mt-2" disabled={loading}>
                                {loading ? "Sending..." : "Send OTP"}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleSubmitReset(onResetSubmit)} className="space-y-4">
                            <div>
                                <Label htmlFor="otp">OTP</Label>
                                <Input
                                    id="otp"
                                    placeholder="Enter 6-digit OTP"
                                    {...registerReset("otp")}
                                    disabled={loading}
                                />
                                {resetErrors.otp && (
                                    <p className="text-red-500 text-sm mt-1">{resetErrors.otp.message}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="new_password">New Password</Label>
                                <div className="relative mt-1">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="new_password"
                                        type="password"
                                        placeholder="Min 8 chars, 1 Upper, 1 Special"
                                        className="pl-10"
                                        {...registerReset("new_password")}
                                        disabled={loading}
                                    />
                                </div>
                                {resetErrors.new_password && (
                                    <p className="text-red-500 text-sm mt-1">{resetErrors.new_password.message}</p>
                                )}
                            </div>

                            <div>
                                <Label htmlFor="confirm_password">Confirm New Password</Label>
                                <div className="relative mt-1">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="confirm_password"
                                        type="password"
                                        placeholder="Re-enter password"
                                        className="pl-10"
                                        {...registerReset("confirm_password")}
                                        disabled={loading}
                                    />
                                </div>
                                {resetErrors.confirm_password && (
                                    <p className="text-red-500 text-sm mt-1">{resetErrors.confirm_password.message}</p>
                                )}
                            </div>

                            {resetErrors.root && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-700 text-sm">{resetErrors.root.message}</p>
                                </div>
                            )}

                            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 mt-2" disabled={loading}>
                                {loading ? "Resetting..." : "Reset Password"}
                            </Button>

                            <div className="text-center mt-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="text-xs text-blue-600 hover:underline"
                                    disabled={loading}
                                >
                                    Change Email/Phone
                                </button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ForgotPassword;
